import { useCallback, useEffect, useState } from 'react';
import {
  connectGraph,
  ensureGraphReady,
  graphAccount,
  graphToken,
  takeGraphReturnUrl,
} from '../lib/graphAuth';
import { callTool, initialize } from '../lib/graphMcp';

/**
 * Standalone end-to-end demo of the deployed niobe-mcp-graph (Softeria M365 MCP), mounted at
 * `?sp=1` (see main.tsx) so it is fully independent of the Niobe backend / primary auth.
 *
 * Flow: connect to Microsoft 365 (delegated, via the Niobe app registration) → the browser calls the
 * remote MCP through the /graph-mcp Vite proxy with the member's Graph token → SharePoint tools run
 * AS the member (per-user ACL). This proves the BYOT pipeline end to end on Niobe's own identity.
 */

interface Site {
  id: string;
  displayName?: string;
  name?: string;
  webUrl?: string;
}
interface SpList {
  id: string;
  displayName?: string;
  name?: string;
  /** Graph list template: 'genericList', 'documentLibrary', 'accessRequest', … Only generic lists
   *  accept item CRUD via this API (libraries require the OneDrive/drive API). */
  list?: { template?: string };
}
interface Drive {
  id: string;
  name?: string;
  webUrl?: string;
  driveType?: string;
}
interface CrudStep {
  label: string;
  ok: boolean;
  detail: string;
}

const asId = (d: unknown): string | undefined => (d as { id?: string } | null)?.id;
const asName = (d: unknown): string | undefined => (d as { name?: string } | null)?.name;
const asSize = (d: unknown): number | undefined => (d as { size?: number } | null)?.size;
const asTitle = (d: unknown): string | undefined => (d as { fields?: { Title?: string } } | null)?.fields?.Title;

function extractValue<T>(data: unknown): T[] {
  if (data && typeof data === 'object' && 'value' in data && Array.isArray((data as { value: unknown }).value)) {
    return (data as { value: T[] }).value;
  }
  return [];
}
function errorText(data: unknown): string | null {
  if (data && typeof data === 'object' && 'error' in data) {
    const e = (data as { error: unknown }).error;
    return typeof e === 'string' ? e : JSON.stringify(e);
  }
  return null;
}

/** Decode a JWT's `scp` (delegated scopes) for diagnostics — does the token actually carry Sites.*? */
function tokenScopes(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.scp ?? payload.roles ?? '(none)');
  } catch {
    return '(unparseable)';
  }
}

/** Small "copy this id" affordance — the ids feed the crud-test script's SITE_ID/DRIVE_ID/LIST_ID. */
function CopyId({ id }: { id: string }): React.ReactElement {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      title={id}
      onClick={() => {
        void navigator.clipboard.writeText(id);
        setDone(true);
        window.setTimeout(() => setDone(false), 1200);
      }}
      style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: 4, padding: '0 .35rem', fontSize: '.7rem', color: '#475569', cursor: 'pointer' }}
    >
      {done ? 'copied ✓' : 'copy id'}
    </button>
  );
}

export function SharePointDemo(): React.ReactElement {
  const [account, setAccount] = useState(() => graphAccount());
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sites, setSites] = useState<Site[] | null>(null);
  const [lists, setLists] = useState<Record<string, SpList[]>>({});
  const [drives, setDrives] = useState<Record<string, Drive[]>>({});
  const [crud, setCrud] = useState<Record<string, CrudStep[]>>({});
  const [fileCrud, setFileCrud] = useState<Record<string, CrudStep[]>>({});
  const [raw, setRaw] = useState<string>('');

  const run = useCallback(async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, []);

  // On mount, complete any redirect sign-in in progress (and pick up an existing session).
  useEffect(() => {
    let cancelled = false;
    void ensureGraphReady()
      .then((acc) => {
        if (!cancelled && acc) setAccount(acc);
        // The code is now redeemed on the reply-URL load rather than after a walk back to where
        // the sign-in started (lib/graphAuth.ts#client), so the address bar is still sitting on
        // the app root carrying a spent `#code`. This page IS the demo already — only the URL is
        // wrong — so correct it in place: replaceState, not a navigation, which would reload the
        // app for nothing and put a consumed response in the history.
        if (!cancelled && window.location.search !== '?sp=1') {
          window.history.replaceState(null, '', takeGraphReturnUrl());
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onConnect = () =>
    run('connect', async () => {
      await connectGraph(); // navigates away unless already connected
      const acc = graphAccount();
      if (acc) setAccount(acc);
    });

  // Copy the current Graph token so it can be pasted as GRAPH_TOKEN into the crud-test script.
  const onCopyToken = async (): Promise<void> => {
    setError(null);
    try {
      const t = await graphToken();
      await navigator.clipboard.writeText(t);
      setNotice(`Graph token copied (scopes: ${tokenScopes(t)}). Paste as GRAPH_TOKEN — valid ~1h.`);
      window.setTimeout(() => setNotice(null), 6000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onSearchSites = () =>
    run('sites', async () => {
      const token = await graphToken();
      // Diagnostic: prove the token carries the SharePoint scope (403s are almost always this).
      console.info('[sp] token scopes =', tokenScopes(token));
      await initialize(token);
      // Delegated search only. (The blank-box "enumerate all" path used /sites/delta, which is
      // APPLICATION-only and 403s on a delegated token — removed. Type a term to search by name.)
      const term = query.trim();
      if (!term) throw new Error('Type part of a site name to search (SharePoint has no delegated "list all sites" call).');
      const res = await callTool(token, 'search-sharepoint-sites', { search: term, top: 50 });
      setRaw(res.raw);
      const err = res.isError ? errorText(res.data) ?? res.raw : null;
      if (err) throw new Error(err);
      setSites(extractValue<Site>(res.data));
      setLists({});
    });

  const onLoadLists = (siteId: string) =>
    run(`lists:${siteId}`, async () => {
      const token = await graphToken();
      const res = await callTool(token, 'list-sharepoint-site-lists', { siteId, top: 50 });
      setRaw(res.raw);
      const err = res.isError ? errorText(res.data) ?? res.raw : null;
      if (err) throw new Error(err);
      setLists((prev) => ({ ...prev, [siteId]: extractValue<SpList>(res.data) }));
    });

  // Self-cleaning write proof: create → read → update → verify → delete → verify-gone, on one list.
  const onTestCrud = (siteId: string, listId: string, listLabel: string) => {
    const key = `${siteId}:${listId}`;
    if (!window.confirm(`Write CRUD test on list "${listLabel}"?\n\nIt creates a temporary item, updates it, then deletes it (self-cleaning).`)) {
      return;
    }
    void run(`crud:${key}`, async () => {
      const token = await graphToken();
      const steps: CrudStep[] = [];
      const push = (label: string, ok: boolean, detail: string): void => {
        steps.push({ label, ok, detail });
        setCrud((p) => ({ ...p, [key]: [...steps] }));
      };
      const call = (name: string, args: Record<string, unknown>): ReturnType<typeof callTool> =>
        callTool(token, name, args);

      const title = `niobe-crud-test ${new Date().toISOString()} (safe to delete)`;

      // CREATE
      let res = await call('create-sharepoint-list-item', { siteId, listId, confirm: true, body: { fields: { Title: title } } });
      const itemId = asId(res.data);
      push('create', !res.isError && !!itemId, itemId ? `item id ${itemId}` : errorText(res.data) ?? res.raw);
      if (!itemId) return;

      // READ
      res = await call('get-sharepoint-site-list-item', { siteId, listId, listItemId: itemId });
      push('read', !res.isError && asTitle(res.data) === title, `Title = ${JSON.stringify(asTitle(res.data))}`);

      // UPDATE
      const updated = `${title} [updated]`;
      res = await call('update-sharepoint-list-item', { siteId, listId, listItemId: itemId, confirm: true, body: { fields: { Title: updated } } });
      push('update', !res.isError, res.isError ? errorText(res.data) ?? res.raw : 'ok');

      // VERIFY UPDATE
      res = await call('get-sharepoint-site-list-item', { siteId, listId, listItemId: itemId });
      push('verify update', asTitle(res.data) === updated, `Title = ${JSON.stringify(asTitle(res.data))}`);

      // DELETE
      res = await call('delete-sharepoint-list-item', { siteId, listId, listItemId: itemId, confirm: true });
      push('delete', !res.isError, res.isError ? errorText(res.data) ?? res.raw : 'ok');

      // VERIFY GONE (a read should now fail)
      res = await call('get-sharepoint-site-list-item', { siteId, listId, listItemId: itemId });
      push('verify gone', res.isError, res.isError ? 'item no longer found ✓' : 'STILL EXISTS ✗');
    });
  };

  const onLoadDrives = (siteId: string) =>
    run(`drives:${siteId}`, async () => {
      const token = await graphToken();
      const res = await callTool(token, 'list-sharepoint-site-drives', { siteId, top: 50 });
      setRaw(res.raw);
      const err = res.isError ? errorText(res.data) ?? res.raw : null;
      if (err) throw new Error(err);
      setDrives((prev) => ({ ...prev, [siteId]: extractValue<Drive>(res.data) }));
    });

  // Self-cleaning FILE proof on a document library (a drive): upload → read → replace → verify →
  // delete → verify-gone. New file is addressed by path under the drive root: `{rootId}:/{name}:`.
  const onTestFileCrud = (driveId: string, driveLabel: string) => {
    if (!window.confirm(`File CRUD test in library "${driveLabel}"?\n\nUploads a temp .txt file, replaces its content, then deletes it (self-cleaning).`)) {
      return;
    }
    void run(`fcrud:${driveId}`, async () => {
      const token = await graphToken();
      const steps: CrudStep[] = [];
      const push = (label: string, ok: boolean, detail: string): void => {
        steps.push({ label, ok, detail });
        setFileCrud((p) => ({ ...p, [driveId]: [...steps] }));
      };
      const call = (name: string, args: Record<string, unknown>): ReturnType<typeof callTool> =>
        callTool(token, name, args);

      // Drive root id — needed to address a new file by path.
      let res = await call('get-drive-root-item', { driveId });
      const rootId = asId(res.data);
      push('drive root', !res.isError && !!rootId, rootId ? `root id ${rootId}` : errorText(res.data) ?? res.raw);
      if (!rootId) return;

      const name = `niobe-crud-${Date.now()}.txt`;
      const body1 = `Niobe file CRUD test — created ${new Date().toISOString()}`;

      // CREATE (upload by path: {rootId}:/{name}:)
      res = await call('upload-file-content', { driveId, driveItemId: `${rootId}:/${name}:`, confirm: true, body: btoa(body1) });
      const fileId = asId(res.data);
      const size1 = asSize(res.data);
      push('create', !res.isError && !!fileId, fileId ? `${name} · ${size1} bytes` : errorText(res.data) ?? res.raw);
      if (!fileId) return;

      // READ (metadata)
      res = await call('get-drive-item', { driveId, driveItemId: fileId });
      push('read', !res.isError, `name=${asName(res.data)} · ${asSize(res.data)} bytes`);

      // UPDATE (replace content → size changes)
      const body2 = `${body1}\n[updated] ${new Date().toISOString()} — extra bytes`;
      res = await call('upload-file-content', { driveId, driveItemId: fileId, confirm: true, body: btoa(body2) });
      const size2 = asSize(res.data);
      push('update', !res.isError, res.isError ? errorText(res.data) ?? res.raw : `new size ${size2} bytes`);

      // VERIFY (size grew)
      push('verify update', typeof size1 === 'number' && typeof size2 === 'number' && size2 > size1, `size ${size1} → ${size2}`);

      // DELETE
      res = await call('delete-onedrive-file', { driveId, driveItemId: fileId, confirm: true });
      push('delete', !res.isError, res.isError ? errorText(res.data) ?? res.raw : 'ok');

      // VERIFY GONE
      res = await call('get-drive-item', { driveId, driveItemId: fileId });
      push('verify gone', res.isError, res.isError ? 'file no longer found ✓' : 'STILL EXISTS ✗');
    });
  };

  return (
    <main style={{ maxWidth: 860, margin: '0 auto', padding: '2rem 1.25rem', fontFamily: 'system-ui, sans-serif' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>Niobe · SharePoint MCP demo</h1>
      <p style={{ color: '#555', marginTop: '.25rem' }}>
        Delegated (BYOT) read/write to SharePoint via the deployed <code>niobe-mcp-graph</code>. You act
        as yourself — only sites you can access appear.
      </p>

      <section style={{ marginTop: '1.25rem', display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" onClick={onConnect} disabled={busy === 'connect'}
          style={btn(!!account)}>
          {account ? `Connected: ${account.name ?? account.username}` : 'Connect Microsoft 365'}
        </button>
        {account && (
          <>
            <input
              aria-label="Site search"
              placeholder="Type a term to search; blank = list all"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ padding: '.5rem .6rem', border: '1px solid #cbd5e1', borderRadius: 6, minWidth: 260 }}
            />
            <button type="button" onClick={onSearchSites} disabled={busy === 'sites'} style={btn(false)}>
              {busy === 'sites' ? 'Searching…' : 'Search sites'}
            </button>
            <button type="button" onClick={() => void onCopyToken()} style={btn(false, true)}>
              Copy Graph token
            </button>
          </>
        )}
      </section>

      <p aria-live="polite" style={{ minHeight: 20, marginTop: '.75rem', color: error ? '#b91c1c' : notice ? '#15803d' : '#555' }}>
        {error ? `Error: ${error}` : notice ? notice : busy ? `Working… (${busy})` : ''}
      </p>

      {sites && (
        <section style={{ marginTop: '.5rem' }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 600 }}>Sites ({sites.length})</h2>
          {sites.length === 0 && <p style={{ color: '#555' }}>No sites returned. Try a search term.</p>}
          <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: '.5rem', marginTop: '.5rem' }}>
            {sites.map((s) => (
              <li key={s.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '.6rem .75rem' }}>
                <div style={{ fontWeight: 600 }}>{s.displayName ?? s.name ?? s.id}</div>
                {s.webUrl && (
                  <a href={s.webUrl} target="_blank" rel="noreferrer" style={{ fontSize: '.85rem', color: '#2563eb' }}>
                    {s.webUrl}
                  </a>
                )}
                <div style={{ marginTop: '.25rem', fontSize: '.72rem', color: '#94a3b8', display: 'flex', gap: '.4rem', alignItems: 'center' }}>
                  <span>SITE_ID</span> <CopyId id={s.id} />
                </div>
                <div style={{ marginTop: '.4rem', display: 'flex', gap: '.4rem' }}>
                  <button type="button" onClick={() => onLoadLists(s.id)} disabled={busy === `lists:${s.id}`} style={btn(false, true)}>
                    {busy === `lists:${s.id}` ? 'Loading lists…' : 'Show lists'}
                  </button>
                  <button type="button" onClick={() => onLoadDrives(s.id)} disabled={busy === `drives:${s.id}`} style={btn(false, true)}>
                    {busy === `drives:${s.id}` ? 'Loading libraries…' : 'Show libraries (files)'}
                  </button>
                </div>
                {lists[s.id] && (
                  <ul style={{ marginTop: '.5rem', paddingLeft: '1rem', fontSize: '.9rem', listStyle: 'none' }}>
                    {lists[s.id]!.map((l) => {
                      const label = l.displayName ?? l.name ?? l.id;
                      const key = `${s.id}:${l.id}`;
                      const template = l.list?.template ?? 'unknown';
                      const canCrud = template === 'genericList';
                      return (
                        <li key={l.id} style={{ marginBottom: '.35rem' }}>
                          <span>• {label}</span>{' '}
                          <span style={{ color: '#94a3b8', fontSize: '.75rem' }}>[{template}]</span>{' '}
                          {canCrud ? (
                            <button
                              type="button"
                              onClick={() => onTestCrud(s.id, l.id, label)}
                              disabled={busy === `crud:${key}`}
                              style={{ ...btn(false, true), padding: '.15rem .5rem', fontSize: '.75rem' }}
                            >
                              {busy === `crud:${key}` ? 'testing…' : 'Test write CRUD'}
                            </button>
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '.72rem' }}>(item CRUD needs a generic list)</span>
                          )}{' '}
                          {canCrud && (
                            <>
                              <span style={{ color: '#94a3b8', fontSize: '.7rem' }}>LIST_ID</span> <CopyId id={l.id} />
                            </>
                          )}
                          {crud[key] && (
                            <ol style={{ margin: '.35rem 0 .5rem 1rem', padding: 0 }}>
                              {crud[key]!.map((st, i) => (
                                <li key={i} style={{ color: st.ok ? '#15803d' : '#b91c1c' }}>
                                  {st.ok ? '✓' : '✗'} {st.label}: {st.detail}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      );
                    })}
                    {lists[s.id]!.length === 0 && <li style={{ color: '#555' }}>(no lists)</li>}
                  </ul>
                )}
                {drives[s.id] && (
                  <ul style={{ marginTop: '.5rem', paddingLeft: '1rem', fontSize: '.9rem', listStyle: 'none' }}>
                    {drives[s.id]!.map((dr) => {
                      const label = dr.name ?? dr.id;
                      return (
                        <li key={dr.id} style={{ marginBottom: '.35rem' }}>
                          <span>🗂 {label}</span>{' '}
                          <span style={{ color: '#94a3b8', fontSize: '.75rem' }}>[library]</span>{' '}
                          <button
                            type="button"
                            onClick={() => onTestFileCrud(dr.id, label)}
                            disabled={busy === `fcrud:${dr.id}`}
                            style={{ ...btn(false, true), padding: '.15rem .5rem', fontSize: '.75rem' }}
                          >
                            {busy === `fcrud:${dr.id}` ? 'testing…' : 'Test file CRUD'}
                          </button>{' '}
                          <span style={{ color: '#94a3b8', fontSize: '.7rem' }}>DRIVE_ID</span> <CopyId id={dr.id} />
                          {fileCrud[dr.id] && (
                            <ol style={{ margin: '.35rem 0 .5rem 1rem', padding: 0 }}>
                              {fileCrud[dr.id]!.map((st, i) => (
                                <li key={i} style={{ color: st.ok ? '#15803d' : '#b91c1c' }}>
                                  {st.ok ? '✓' : '✗'} {st.label}: {st.detail}
                                </li>
                              ))}
                            </ol>
                          )}
                        </li>
                      );
                    })}
                    {drives[s.id]!.length === 0 && <li style={{ color: '#555' }}>(no libraries)</li>}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {raw && (
        <details style={{ marginTop: '1.25rem' }}>
          <summary style={{ cursor: 'pointer', color: '#555' }}>Last raw MCP response</summary>
          <pre style={{ overflowX: 'auto', background: '#0f172a', color: '#e2e8f0', padding: '.75rem', borderRadius: 8, fontSize: '.8rem' }}>
            {raw}
          </pre>
        </details>
      )}
    </main>
  );
}

function btn(connected: boolean, subtle = false): React.CSSProperties {
  return {
    padding: '.5rem .85rem',
    borderRadius: 6,
    border: '1px solid ' + (subtle ? '#cbd5e1' : 'transparent'),
    background: subtle ? '#fff' : connected ? '#16a34a' : '#2563eb',
    color: subtle ? '#0f172a' : '#fff',
    fontWeight: 600,
    cursor: 'pointer',
  };
}
